import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { adsMemberApi, isConflictError } from '../../api/adsMemberApi';
import CampaignBillingCard from './CampaignBillingCard';
import CampaignReportingCard from './CampaignReportingCard';
import './ads.css';

export default function CampaignDetailPage() {
  const { campaignId } = useParams();
  const { t } = useTravelTranslation();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    setConflict(false);

    adsMemberApi.campaigns
      .get(campaignId)
      .then((data) => {
        setCampaign(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || '載入廣告活動詳情失敗');
        setLoading(false);
      });
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const executeAction = async (actionFn, actionName) => {
    if (!campaign) return;
    setActionLoading(true);
    setError('');
    setConflict(false);

    try {
      const res = await actionFn(campaignId, {
        expectedRowVersion: campaign.rowVersion,
      });
      setCampaign(res);
      setActionLoading(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflict(true);
      } else {
        setError(err?.message || `${actionName}失敗`);
      }
      setActionLoading(false);
    }
  };

  const handlePrepare = () =>
    executeAction(adsMemberApi.campaigns.prepare, t('travel.ads.campaigns.prepare'));

  const handleActivate = () =>
    executeAction(adsMemberApi.campaigns.activate, t('travel.ads.campaigns.activate'));

  const handlePause = () =>
    executeAction(adsMemberApi.campaigns.pause, t('travel.ads.campaigns.pause'));

  const handleCancel = () =>
    executeAction(adsMemberApi.campaigns.cancel, t('travel.ads.campaigns.cancel'));

  if (loading) {
    return (
      <main className="ads-page" data-testid="campaign-detail-loading">
        <div className="ads-loading">{t('travel.ads.common.loading')}</div>
      </main>
    );
  }

  if (error && !campaign) {
    return (
      <main className="ads-page" data-testid="campaign-detail-error">
        <div className="ads-alert ads-alert--error">{error}</div>
        <Link to="/me/ads" className="ads-btn ads-btn--secondary">
          {t('travel.ads.common.backToAds')}
        </Link>
      </main>
    );
  }

  const status = campaign?.status || 'DRAFT';
  const isDraft = status === 'DRAFT';
  const isReady = status === 'READY';
  const isActive = status === 'ACTIVE';
  const isPaused = status === 'PAUSED';
  const isSuspended = status === 'SUSPENDED';

  return (
    <main className="ads-page" data-testid="campaign-detail-page">
      <header className="ads-header">
        <div>
          <h1 className="ads-title">廣告活動 #{campaign?.campaignId}</h1>
          <span className={`ads-badge ads-badge--${status}`} data-testid="campaign-status-badge">
            {t(`travel.ads.status.${status}`) || status}
          </span>
        </div>
        <div className="ads-header__actions">
          <Link to="/me/ads" className="ads-btn ads-btn--secondary">
            {t('travel.ads.common.backToAds')}
          </Link>
        </div>
      </header>

      {conflict ? (
        <div className="ads-alert ads-alert--conflict" data-testid="campaign-conflict-alert">
          <span>{t('travel.ads.conflictMessage')}</span>
          <button
            type="button"
            className="ads-btn ads-btn--secondary"
            onClick={load}
            data-testid="btn-campaign-refresh"
          >
            {t('travel.ads.common.refresh')}
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="ads-alert ads-alert--error" data-testid="campaign-action-error">
          {error}
        </div>
      ) : null}

      {isSuspended ? (
        <div className="ads-alert ads-alert--notice" data-testid="campaign-suspended-notice">
          {t('travel.ads.campaigns.suspendedNotice')}
        </div>
      ) : null}

      <section className="ads-detail-section">
        <h4 className="ads-detail-section__title">活動設定</h4>
        <div className="ads-grid-2">
          <div className="ads-metric">
            <div className="ads-metric__label">活動 ID</div>
            <div className="ads-metric__value" data-testid="campaign-id">
              {campaign?.campaignId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">推廣申請 ID</div>
            <div className="ads-metric__value" data-testid="campaign-promotion-id">
              {campaign?.promotionApplicationId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">素材 ID</div>
            <div className="ads-metric__value" data-testid="campaign-creative-id">
              {campaign?.creativeId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">版本 (rowVersion)</div>
            <div className="ads-metric__value" data-testid="campaign-row-version">
              {campaign?.rowVersion}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <p><strong>排期時間：</strong><span data-testid="campaign-dates">{campaign?.startAt} ~ {campaign?.endAt}</span></p>
          <p><strong>定位模式：</strong><span data-testid="campaign-targeting-mode">{campaign?.targetingMode}</span></p>
          <p><strong>頻次上限：</strong>每日 {campaign?.maxImpressionsPerMemberPerDay} 次 / 每會話 {campaign?.maxImpressionsPerMemberPerSession} 次 / 間隔 {campaign?.minMinutesBetweenImpressions} 分鐘</p>
        </div>
      </section>

      <section className="ads-detail-section">
        <h4 className="ads-detail-section__title">生命週期操作</h4>
        <div className="ads-header__actions">
          {isDraft ? (
            <button
              type="button"
              className="ads-btn ads-btn--primary"
              onClick={handlePrepare}
              disabled={actionLoading}
              data-testid="btn-campaign-prepare"
            >
              {t('travel.ads.campaigns.prepare')}
            </button>
          ) : null}

          {isReady || isPaused ? (
            <button
              type="button"
              className="ads-btn ads-btn--primary"
              onClick={handleActivate}
              disabled={actionLoading}
              data-testid="btn-campaign-activate"
            >
              {t('travel.ads.campaigns.activate')}
            </button>
          ) : null}

          {isActive ? (
            <button
              type="button"
              className="ads-btn ads-btn--secondary"
              onClick={handlePause}
              disabled={actionLoading}
              data-testid="btn-campaign-pause"
            >
              {t('travel.ads.campaigns.pause')}
            </button>
          ) : null}

          {isDraft || isReady || isActive || isPaused ? (
            <button
              type="button"
              className="ads-btn ads-btn--danger"
              onClick={handleCancel}
              disabled={actionLoading}
              data-testid="btn-campaign-cancel"
            >
              {t('travel.ads.campaigns.cancel')}
            </button>
          ) : null}
        </div>
      </section>

      {/* 內嵌帳務摘要卡片 */}
      <CampaignBillingCard campaignId={campaignId} />

      {/* 內嵌成效摘要卡片 */}
      <CampaignReportingCard
        campaignId={campaignId}
        startAt={campaign?.startAt}
        endAt={campaign?.endAt}
      />
    </main>
  );
}
